// ---------------------------------------------------------------------------
// Keyless weather helpers — Open-Meteo (geocoding + forecast).
// No API key required; both endpoints are CORS-friendly and fetched directly
// from the browser. Shared by the client VoucherPreview weather section and
// the Dashboard departures weather chips.
// ---------------------------------------------------------------------------
import React from 'react';
import {
  Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow,
  CloudLightning,
} from 'lucide-react';

// WMO weather interpretation codes → human label
const WMO_LABEL = {
  0: 'Clear sky',
  1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Rime fog',
  51: 'Light drizzle', 53: 'Drizzle', 55: 'Dense drizzle',
  56: 'Freezing drizzle', 57: 'Freezing drizzle',
  61: 'Light rain', 63: 'Rain', 65: 'Heavy rain',
  66: 'Freezing rain', 67: 'Freezing rain',
  71: 'Light snow', 73: 'Snow', 75: 'Heavy snow', 77: 'Snow grains',
  80: 'Rain showers', 81: 'Rain showers', 82: 'Heavy showers',
  85: 'Snow showers', 86: 'Snow showers',
  95: 'Thunderstorm', 96: 'Thunderstorm + hail', 99: 'Thunderstorm + hail',
};

export function weatherLabel(code) {
  return WMO_LABEL[code] != null ? WMO_LABEL[code] : '—';
}

function iconFor(code) {
  if (code === 0) return Sun;
  if (code === 1 || code === 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if ([51, 53, 55, 56, 57].includes(code)) return CloudDrizzle;
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return CloudRain;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return CloudSnow;
  if ([95, 96, 99].includes(code)) return CloudLightning;
  return Cloud;
}

// Reusable weather glyph (lucide) for a given WMO code.
export const WeatherIcon = ({ code, ...rest }) => {
  const Icon = iconFor(code);
  return <Icon {...rest} />;
};

// ---------------------------------------------------------------------------
// City extraction — destination tokens first, then distinct day-activity
// locations. Cleans noise so we never geocode descriptions / times.
// ---------------------------------------------------------------------------
function cleanToken(raw) {
  const v = String(raw || '').replace(/\s+/g, ' ').trim();
  if (!v) return '';
  if (v.length < 2 || v.length > 40) return '';
  if (v.split(' ').length > 4) return '';          // likely a description
  if (/\d{1,2}:\d{2}/.test(v)) return '';           // a time, not a place
  if (/^\d+$/.test(v)) return '';                   // pure number
  return v;
}

export function extractWeatherCities(destination, days, max = 6) {
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const v = cleanToken(raw);
    if (!v) return;
    const key = v.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(v);
  };

  // Destination string → split on common separators.
  String(destination || '')
    .split(/[,/&+·]|\sand\s|\sto\s/i)
    .forEach(push);

  // Distinct activity locations across all days.
  const dayList = Array.isArray(days) ? days : [];
  for (const d of dayList) {
    const acts = Array.isArray(d.activities) ? d.activities : [];
    for (const a of acts) push(a.location);
  }

  return out.slice(0, max);
}

// ---------------------------------------------------------------------------
// Network — geocode + current weather, both module-cached so repeated cities
// (across departure rows) hit the network once per session.
// ---------------------------------------------------------------------------
const geoCache = new Map();
const wxCache = new Map();

export async function geocodeCity(name) {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return null;
  if (geoCache.has(key)) return geoCache.get(key);
  try {
    const res = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=10&language=en&format=json`,
    );
    const data = await res.json();
    const results = Array.isArray(data.results) ? data.results : [];
    if (!results.length) { geoCache.set(key, null); return null; }
    // Highest-population match → best fit for well-known travel destinations
    // (disambiguates e.g. "Bali, Indonesia" from "Bali, West Bengal").
    const best = results.reduce((a, b) => ((b.population || 0) > (a.population || 0) ? b : a));
    const out = {
      lat: best.latitude, lon: best.longitude,
      name: best.name, country: best.country,
    };
    geoCache.set(key, out);
    return out;
  } catch {
    geoCache.set(key, null);
    return null;
  }
}

export async function fetchWeather(lat, lon) {
  const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;
  if (wxCache.has(key)) return wxCache.get(key);
  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}`
      + '&current=temperature_2m,weather_code,relative_humidity_2m,wind_speed_10m'
      + '&daily=weather_code,temperature_2m_max,temperature_2m_min'
      + '&timezone=auto&forecast_days=7';
    const res = await fetch(url);
    const data = await res.json();
    wxCache.set(key, data);
    return data;
  } catch {
    return null;
  }
}

// City name → { city, country, current, daily } (or null if it can't resolve).
export async function getCityWeather(name) {
  const geo = await geocodeCity(name);
  if (!geo) return null;
  const wx = await fetchWeather(geo.lat, geo.lon);
  if (!wx || !wx.current) return null;
  return {
    city: geo.name || name,
    country: geo.country || '',
    current: wx.current,
    daily: wx.daily || null,
  };
}
